// play-metric/src/lib/moduleRegistry.ts
/**
 * The one list of modules. A federation (body) runs federation modules, resolved by the SQL
 * function `public.federation_module_enabled`; a club affiliation type grants club modules.
 * `federationModules.ts` and `membersCatalogue.ts` derive their lists and defaults from here, so
 * a key is declared exactly once.
 *
 * `offered: false` keeps a key valid in the database (check constraint, SQL resolver) without
 * showing it in the admin switches or accepting it in a PUT. `whitelabel` is one such key;
 * `leagues` (both owners) is another until the league screens ship.
 *
 * Order matters: it is the order of the admin switches and of the club type editor.
 */
export interface ModuleEntry {
  readonly key: string;
  readonly owner: 'federation' | 'club';
  /** What the resolver returns with no stored row (federation modules only; ignored for club). */
  readonly defaultOn: boolean;
  readonly offered: boolean;
}

export const MODULE_REGISTRY = [
  // Federation modules (federation_module.module)
  { key: 'events', owner: 'federation', defaultOn: true, offered: true },
  { key: 'ladder', owner: 'federation', defaultOn: true, offered: true },
  { key: 'affiliations', owner: 'federation', defaultOn: false, offered: true },
  { key: 'clubs', owner: 'federation', defaultOn: true, offered: true },
  { key: 'leagues', owner: 'federation', defaultOn: false, offered: false },
  { key: 'club_affiliations', owner: 'federation', defaultOn: false, offered: true },
  { key: 'whitelabel', owner: 'federation', defaultOn: false, offered: false },
  // Club modules (club_affiliation_type.modules)
  { key: 'events', owner: 'club', defaultOn: false, offered: true },
  { key: 'ladder', owner: 'club', defaultOn: false, offered: true },
  { key: 'club_portal', owner: 'club', defaultOn: false, offered: true },
  { key: 'leagues', owner: 'club', defaultOn: false, offered: false },
  { key: 'streaming', owner: 'club', defaultOn: false, offered: true },
  { key: 'coaching', owner: 'club', defaultOn: false, offered: true },
] as const satisfies readonly ModuleEntry[];

type Entry = (typeof MODULE_REGISTRY)[number];

/** Keys of the offered entries of one owner, in registry order, typed as a literal union. */
type OfferedKeys<O extends Entry['owner']> = Extract<Entry, { owner: O; offered: true }>['key'];

function offeredKeys<O extends Entry['owner']>(owner: O): readonly OfferedKeys<O>[] {
  return MODULE_REGISTRY.filter((m) => m.owner === owner && m.offered).map(
    (m) => m.key,
  ) as OfferedKeys<O>[];
}

/**
 * Every club key the database accepts, offered or not: the club resolver (`club_module_state`)
 * answers for each of them, so its callers type their answers with this, not with the offered list.
 */
export type KnownClubModule = Extract<Entry, { owner: 'club' }>['key'];

export const FEDERATION_MODULE_KEYS = offeredKeys('federation');
export const CLUB_MODULE_KEYS = offeredKeys('club');

/** Registry default for an offered federation module. */
export function federationModuleDefault(key: OfferedKeys<'federation'>): boolean {
  const e = MODULE_REGISTRY.find((m) => m.owner === 'federation' && m.key === key);
  return e?.defaultOn ?? false;
}
